import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { TEXT_PREVIEW_BYTES } from "@via/pages/projects/runs/reports/utils/constants.ts";
import { getReportUrl } from "@via/pages/projects/runs/reports/utils/helpers.ts";
import { useFetchRunReportTabularPreview } from "@via/pages/projects/runs/runs.api.tsx";
import { formatFileSize } from "@via/utils/helpers.tsx";
import { RunReportResource } from "common";
import { JSX } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";

// Relative links and images in a report (`![](plots/pca.png)`) point at files
// next to the report, so resolve them against the report's own URL rather
// than the SPA route. `defaultUrlTransform` still drops `javascript:` and
// other unsafe schemes.
export const resolveReportUrl = (url: string, reportUrl: string): string => {
  const safe = defaultUrlTransform(url);
  if (!safe || safe.startsWith("#") || !reportUrl) return safe;
  try {
    return new URL(safe, new URL(reportUrl, window.location.href)).href;
  } catch {
    return safe;
  }
};

function TextReportBody({
  report,
  markdown,
}: {
  report: RunReportResource;
  markdown: boolean;
}): JSX.Element {
  const url = getReportUrl(report?.routePath);
  // Always a bounded byte prefix (the same Range fetch the tabular preview
  // uses), so a multi-gigabyte log never lands in the browser in full.
  const {
    data: content,
    isLoading,
    isError,
  } = useFetchRunReportTabularPreview(url, TEXT_PREVIEW_BYTES, {
    enabled: !!url,
  });
  const truncated = (report?.fileSize ?? 0) > TEXT_PREVIEW_BYTES;

  if (isLoading) {
    return <SkeletonLoader count={3} />;
  }

  if (isError) {
    return <GenericError message={"Error While Loading The File"} />;
  }

  return (
    <div className="h-full overflow-auto pb-15">
      {truncated && (
        <div className="mb-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          This file is too large to open in full. Showing the first{" "}
          {formatFileSize(TEXT_PREVIEW_BYTES)}. Use the download button for the
          complete file.
        </div>
      )}
      {markdown ? (
        <article
          data-testid="markdown-report"
          className="prose prose-sm max-w-none rounded-md border border-gray-200 bg-white p-6 prose-pre:whitespace-pre-wrap prose-table:block prose-table:overflow-x-auto">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            urlTransform={value => resolveReportUrl(value, url)}
            components={{
              a: props => (
                <a {...props} target="_blank" rel="noreferrer noopener" />
              ),
            }}>
            {content ?? ""}
          </ReactMarkdown>
        </article>
      ) : (
        <pre
          data-testid="text-report"
          className="whitespace-pre-wrap break-words rounded-md border border-gray-200 bg-gray-50 p-4 font-mono text-xs leading-relaxed text-gray-800">
          {content ?? ""}
        </pre>
      )}
    </div>
  );
}

export function TextReport({
  report,
}: {
  report: RunReportResource;
}): JSX.Element {
  return <TextReportBody report={report} markdown={false} />;
}

export function MarkdownReport({
  report,
}: {
  report: RunReportResource;
}): JSX.Element {
  return <TextReportBody report={report} markdown={true} />;
}
